import axios, { AxiosError } from 'axios';
import type { InternalAxiosRequestConfig } from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';

const baseURL = import.meta.env.VITE_OAPI_ENDPOINT || '';
const cognitoEndpoint = import.meta.env.VITE_COGNITO_ENDPOINT || '';

const axiosInstance = axios.create({
  baseURL,
  headers: { accept: 'application/json' },
  timeout: 10000,
});

// Attach access token from Amplify session on every request.
// Amplify's fetchAuthSession() returns a cached token and automatically
// refreshes it before expiry — no manual refresh timer needed.
axiosInstance.interceptors.request.use(
  async request => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (token) {
        request.headers['Authorization'] = token;
      }
    } catch {
      // Not authenticated — request proceeds without token; server will 401
    }
    return request;
  },
  error => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    const status = error.response?.status;

    // On 401/403, force-refresh the Amplify session and retry once.
    // Amplify handles the actual token exchange internally.
    if ((status === 401 || status === 403) && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const session = await fetchAuthSession({ forceRefresh: true });
        const newToken = session.tokens?.accessToken?.toString();

        if (newToken) {
          originalRequest.headers['Authorization'] = newToken;
          return axiosInstance(originalRequest);
        }
      } catch {
        // Refresh failed — fall through to logout redirect
      }

      // No valid session — redirect to login
      window.location.href = '/';
    }

    return Promise.reject(error?.response?.data || error);
  },
);

// Unauthenticated Axios client for Cognito API calls (SignUp, UpdateUserAttributes, etc.)
const authAxiosClient = axios.create({
  baseURL: cognitoEndpoint,
  timeout: 30000,
});

authAxiosClient.interceptors.response.use(
  response => response,
  (error: AxiosError<any>) => {
    return Promise.reject(error?.response?.data || error);
  },
);

export { axiosInstance, authAxiosClient };
